import React, { memo, useCallback, useRef } from 'react'
import { FormattedMessage } from 'react-intl'
import { intl } from 'lang/intl'

import { TtButton, TtDropdown, TtIcon, TtSkeleton } from '@23/ui-react'

import { getRole } from '../../../global/blocs/roomInfo'
import { WebinarInfoSidebarComponent } from '../../../sharedLegacy/components/WebinarInfoSidebarComponent/WebinarInfoSidebarComponent'
import {
  useAddCommentReactionMutation,
  useDeleteCommentMutation,
  useMakeCommentIntoQuestionMutation,
  usePinCommentMutation,
  useRemoveCommentReactionMutation,
} from '../../blocs/useChatQuery'
import { ChatReactionsSelector } from '../ChatReactions/ChatReactionsSelector'
import { selectReplyComment } from '../Reply/Reply'
import { useDeleteChatMessageConfirmation } from '../DeleteChatMessageConfirmation/DeleteCommentConfirmation'
import {
  RESERVED_BOT_GREETING_MESSAGE_ID,
  RESERVED_WEBINAR_INFO_MESSAGE_ID,
  useChatMessage,
  useOwnReactions,
} from '../../blocs/useChat'
import { useCanInteractWithChat } from '../../blocs/chatVisibility'
import { formatSenderName } from '../formatSenderName'

import { GreetingMessage } from './GreetingMessage/GreetingMessage'

import './ChatMessage.scss'

interface ChatMessageProps {
  id: number
}

const ChatMessage = ({ id }: ChatMessageProps) => {
  const message = useChatMessage(id)
  const reactions = useOwnReactions(id)

  const canInteractWithChat = useCanInteractWithChat()

  const { mutate: addReaction } = useAddCommentReactionMutation(id)
  const { mutate: removeReaction } = useRemoveCommentReactionMutation(id)
  const { mutate: pinComment } = usePinCommentMutation(id)

  const deleteChatMessageConfirmation = useDeleteChatMessageConfirmation()

  const { mutate: deleteComment } = useDeleteCommentMutation(id)
  const {
    mutate: makeCommentIntoQuestion,
    isLoading: isTurningCommentIntoQuestion,
  } = useMakeCommentIntoQuestionMutation(id)

  const primaryAddReactionDropdown = useRef<HTMLTtDropdownElement>(null)
  const secondaryAddReactionDropdown = useRef<HTMLTtDropdownElement>(null)

  const closeReactionsDropdowns = useCallback(() => {
    void primaryAddReactionDropdown.current?.hide()
    void secondaryAddReactionDropdown.current?.hide()
  }, [])

  const onReactionSelected = useCallback(
    (emoji: string) => {
      if (reactions?.has(emoji)) {
        removeReaction(emoji)
      } else {
        addReaction(emoji)
      }
      closeReactionsDropdowns()
    },
    [addReaction, closeReactionsDropdowns, reactions, removeReaction]
  )

  const onDeleteClicked = useCallback(async () => {
    const confirmed = await deleteChatMessageConfirmation()
    if (confirmed) deleteComment()
  }, [deleteChatMessageConfirmation, deleteComment])

  const onReplyClicked = useCallback(() => {
    selectReplyComment(id)
    void document
      .querySelector<HTMLTtTextFieldElement>('.chat-form-text-field')
      ?.setFocus()
  }, [id])

  if (!message) return null

  const senderName = formatSenderName(message.name, message.fullName)

  const replyToName = formatSenderName(
    message.reply?.name ?? '',
    message.reply?.fullName ?? ''
  )

  return (
    <div className="chat-message" data-pinned={message.pinned}>
      {message.reply && (
        <div className="chat-message-reply-container">
          <div className="chat-message-reply">
            <div className="chat-message-reply-sender">
              <span className="chat-message-reply-sender-name">
                {replyToName}
              </span>
              {message.reply.label === 'speaker' && (
                <span className="chat-message-reply-sender-label">
                  <FormattedMessage defaultMessage="Speaker" />
                </span>
              )}
            </div>
            <div className="chat-message-reply-quote">
              {message.reply.content}
            </div>
          </div>
        </div>
      )}
      <div className="chat-message-header">
        <div className="chat-message-sender">
          <span className="chat-message-sender-name">{senderName}</span>
          {message.label === 'speaker' && (
            <span className="chat-message-sender-label">
              <FormattedMessage defaultMessage="Speaker" />
            </span>
          )}
        </div>
        {!message.isOptimisticUpdate && canInteractWithChat && (
          <div className="chat-message-actions">
            <TtDropdown
              placement="top-end"
              ref={primaryAddReactionDropdown}
              hoist
            >
              <TtButton
                className="chat-message-reaction-actions-react"
                slot="trigger"
                color="secondary"
                variant="light"
                size="x-small"
                circle
              >
                <TtIcon library="one/webinars" name="add-reaction" />
              </TtButton>

              <ChatReactionsSelector onReactionSelected={onReactionSelected} />
            </TtDropdown>

            <TtButton
              className="chat-message-reaction-actions-reply"
              color="secondary"
              variant="light"
              size="x-small"
              circle
              onClick={onReplyClicked}
            >
              <TtIcon library="one/webinars" name="chat" />
            </TtButton>

            {getRole() === 'producer' && (
              <>
                <TtButton
                  color="secondary"
                  variant="light"
                  size="x-small"
                  circle
                  label={intl.formatMessage({
                    defaultMessage: 'Turn into a question',
                  })}
                  disabled={isTurningCommentIntoQuestion}
                  onClick={() => makeCommentIntoQuestion()}
                >
                  <TtIcon library="one/webinars" name="question" />
                </TtButton>

                <TtButton
                  color="secondary"
                  variant="light"
                  size="x-small"
                  circle
                  label={
                    message.pinned
                      ? intl.formatMessage({ defaultMessage: 'Unpin message' })
                      : intl.formatMessage({ defaultMessage: 'Pin message' })
                  }
                  onClick={() => pinComment(!message.pinned)}
                >
                  <TtIcon library="one/webinars" name="pin" />
                </TtButton>

                <TtButton
                  color="secondary"
                  variant="light"
                  size="x-small"
                  circle
                  label={intl.formatMessage({
                    defaultMessage: 'Delete message',
                  })}
                  onClick={onDeleteClicked}
                >
                  <TtIcon library="one" name="bin" />
                </TtButton>
              </>
            )}
          </div>
        )}
      </div>

      <div
        className="chat-message-content"
        // oxlint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{ __html: message.htmlContent }}
      />

      {message.media && (
        <div className="chat-media-link">
          {message.media.thumbnailUrl && (
            <a href={message.media.url} target="_blank" rel="noreferrer">
              <img src={message.media.thumbnailUrl} alt={message.media.title} />
            </a>
          )}
          <div className="chat-media-link-info">
            <div className="chat-media-link-title">{message.media.title}</div>
            <div className="chat-media-link-description">
              {message.media.description}
            </div>
            <a
              href={message.media.url}
              className="chat-media-url"
              target="_blank"
              rel="noreferrer"
            >
              {message.media.providerUrl}
            </a>
          </div>
        </div>
      )}

      {message.reactions.size > 0 && (
        <div className="chat-message-reactions-list">
          {Array.from(message.reactions.entries()).map(([emoji, count]) => (
            <TtButton
              key={emoji}
              variant="light"
              color="secondary"
              size="x-small"
              pill
              data-emoji={emoji}
              data-reacted={reactions?.has(emoji)}
              // @ts-expect-error — inert is a valid html attribute; React is too old to recognize it
              inert={!canInteractWithChat ? 'true' : 'false'}
              onClick={() => {
                onReactionSelected(emoji)
              }}
            >
              <span className="chat-message-reaction-emoji" slot="prefix">
                {emoji}
              </span>
              <span className="chat-message-reaction-count">{count}</span>
            </TtButton>
          ))}
          {canInteractWithChat && (
            <TtDropdown
              placement="top-end"
              ref={secondaryAddReactionDropdown}
              hoist
            >
              <TtButton
                className="chat-message-add-reaction-bottom"
                slot="trigger"
                color="secondary"
                variant="light"
                size="x-small"
                circle
              >
                <TtIcon library="one/webinars" name="add-reaction" />
              </TtButton>

              <ChatReactionsSelector onReactionSelected={onReactionSelected} />
            </TtDropdown>
          )}
        </div>
      )}
    </div>
  )
}

interface ChatMessagePlaceholderProps {
  hash?: number
}

const ChatMessagePlaceholder = ({ hash }: ChatMessagePlaceholderProps) => {
  return (
    <div
      className="chat-message chat-message-placeholder"
      data-5={(hash ?? 0) % 5}
      data-7={(hash ?? 0) % 7}
    >
      <div className="chat-message-header">
        <TtSkeleton />
      </div>
      <div className="chat-message-content">
        <TtSkeleton />
      </div>
    </div>
  )
}

const ChatMessageMultiplexer = ({
  id,
  hash,
}: Partial<ChatMessageProps & ChatMessagePlaceholderProps>) => {
  if (id === undefined) {
    return <ChatMessagePlaceholder hash={hash} />
  }
  switch (id) {
    case RESERVED_WEBINAR_INFO_MESSAGE_ID:
      return <WebinarInfoSidebarComponent />
    case RESERVED_BOT_GREETING_MESSAGE_ID:
      return <GreetingMessage />

    default:
      return <ChatMessage id={id} />
  }
}

const MemoizedChatMessage = memo(ChatMessageMultiplexer)

export { MemoizedChatMessage as ChatMessage }
